import {
  arrayRemove,
  arrayUnion,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * `participants/{uid}/memberships/{districtId}` の `joinedSessionIds` (参加セッション一覧) と
 * `mainSessionId` (投稿に使うメインセッション、案1 統合 2026-06-16) を subscribe / 更新する hook。
 *
 * 旧 SettingsModal.tsx の「参加セッション」section は checkbox で **即時** membership doc を
 * 書き換える UX (Save button を介さない) だった。これを v2 で踏襲するため、本 hook が
 * membership を subscribe し、checkbox toggle で arrayUnion / arrayRemove する。
 *
 * mainSessionId (案1):
 *   - 投稿に使うメインセッション。joinedSessionIds の 1 つを指す。localStorage の旧「現セッション」を
 *     置き換え、ユーザー単位で端末間同期する。
 *   - `setMainSessionId(id)` は id を joinedSessionIds に arrayUnion (未参加なら参加扱い) してから
 *     mainSessionId を id にする (QR/URL からのセッション指定や、復帰ピッカーからの選択にも対応)。
 *   - 有効値の解決 (参加 1 つなら自動 / archive フォールバック等) は shared-types resolveMainSession に集約。
 *
 * 注:
 *   - rules: `participants/{uid}/memberships/{districtId}` の write は本人 + canAccessDistrict のみ
 *     (firestore.rules §411)。本 hook は本人の uid でのみ呼ばれる。
 */
export interface UseMembershipSessionsReturn {
  joinedSessionIds: string[];
  /** membership.mainSessionId の raw 値 (解決前)。有効解決は resolveMainSession を使う。 */
  mainSessionId: string | null;
  /** 初回 snapshot 受領済か (loading 中の誤判定を避けるため)。 */
  loaded: boolean;
  joinSession: (sessionId: string) => Promise<void>;
  leaveSession: (sessionId: string) => Promise<void>;
  /** メインセッションを設定 (id を参加扱いにしてから main にする)。null でメイン解除。 */
  setMainSessionId: (sessionId: string | null) => Promise<void>;
}

export function useMembershipSessions(
  uid: string | null,
  districtId: string | null,
): UseMembershipSessionsReturn {
  const [joinedSessionIds, setJoinedSessionIds] = useState<string[]>([]);
  const [mainSessionId, setMainSessionIdState] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    if (!uid || !districtId) {
      setJoinedSessionIds([]);
      setMainSessionIdState(null);
      return;
    }
    const ref = doc(db, 'participants', uid, 'memberships', districtId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        const ids = Array.isArray(data?.joinedSessionIds)
          ? (data!.joinedSessionIds as unknown[]).filter((x): x is string => typeof x === 'string')
          : [];
        setJoinedSessionIds(ids);
        const raw = data?.mainSessionId;
        setMainSessionIdState(typeof raw === 'string' && raw.length > 0 ? raw : null);
        setLoaded(true);
      },
      () => {
        setJoinedSessionIds([]);
        setMainSessionIdState(null);
        setLoaded(true);
      },
    );
    return () => unsub();
  }, [uid, districtId]);

  const joinSession = useCallback(
    async (sessionId: string) => {
      if (!uid || !districtId) return;
      const ref = doc(db, 'participants', uid, 'memberships', districtId);
      await setDoc(
        ref,
        { joinedSessionIds: arrayUnion(sessionId), lastSeenAt: serverTimestamp() },
        { merge: true },
      );
    },
    [uid, districtId],
  );

  const leaveSession = useCallback(
    async (sessionId: string) => {
      if (!uid || !districtId) return;
      const ref = doc(db, 'participants', uid, 'memberships', districtId);
      // 抜けるセッションがメインだった場合は mainSessionId も解除 (resolveMainSession も
      // 候補外を吸収するが、データを汚さないよう明示クリア)。
      const patch: Record<string, unknown> = {
        joinedSessionIds: arrayRemove(sessionId),
        lastSeenAt: serverTimestamp(),
      };
      if (mainSessionId === sessionId) patch.mainSessionId = null;
      await setDoc(ref, patch, { merge: true });
    },
    [uid, districtId, mainSessionId],
  );

  const setMainSessionId = useCallback(
    async (sessionId: string | null) => {
      if (!uid || !districtId) return;
      const ref = doc(db, 'participants', uid, 'memberships', districtId);
      const patch: Record<string, unknown> = {
        mainSessionId: sessionId,
        lastSeenAt: serverTimestamp(),
      };
      // id 指定時は参加扱いにする (未参加セッションを QR/URL/復帰ピッカーで選んだ場合に対応)。
      if (sessionId) patch.joinedSessionIds = arrayUnion(sessionId);
      await setDoc(ref, patch, { merge: true });
    },
    [uid, districtId],
  );

  return {
    joinedSessionIds,
    mainSessionId,
    loaded,
    joinSession,
    leaveSession,
    setMainSessionId,
  };
}
